import { X, Check } from "lucide-react";
import { motion } from "framer-motion";
import type { AdsMode } from "./AdsModeSelector";

interface CriteriaRow {
  without: string;
  with: string;
}

const baseCriteria: CriteriaRow[] = [
  {
    without: "Le prospect tombe sur une boîte vocale et raccroche",
    with: "Conversation naturelle, adaptée à l'immobilier",
  },
  {
    without: "Vous réécoutez un message vocal quand vous y pensez",
    with: "Fiche structurée : intention, budget, urgence, résumé",
  },
  {
    without: "Saturation aux heures de pointe",
    with: "Aucune saturation, même en pic d'appels",
  },
  {
    without: "Disponible aux horaires bureau",
    with: "24h/24, 7j/7",
  },
  {
    without: "Le prospect retient : indisponibilité",
    with: "Le prospect retient : disponibilité",
  },
];

const locationExtra: CriteriaRow = {
  without: "Aucune qualification des dossiers",
  with: "Solvabilité, projet, urgence, garant",
};

const cardVariants = {
  hidden: (direction: number) => ({
    opacity: 0,
    x: direction * 40,
  }),
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

const DiffContent = ({ mode }: { mode: AdsMode }) => {
  const criteria: CriteriaRow[] =
    mode === "location"
      ? [...baseCriteria.slice(0, 2), locationExtra, ...baseCriteria.slice(2)]
      : baseCriteria;

  return (
    <div>
      <div className="max-w-3xl mx-auto text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-semibold text-foreground">
          Avant Immovox vs Après.
        </h2>
        <p className="text-lg text-muted-foreground mt-4">
          {mode === "vente"
            ? "La différence entre un mandat signé et un mandat perdu."
            : "La différence entre une journée productive et une journée au téléphone."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Sans Immovox */}
        <motion.div
          custom={-1}
          variants={cardVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 md:p-8"
        >
          <h3 className="text-sm font-bold uppercase tracking-widest text-destructive mb-6">
            Sans Immovox
          </h3>
          <ul className="space-y-5">
            {criteria.map((row) => (
              <li key={row.without} className="flex gap-3 items-start">
                <X className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground leading-relaxed">
                  {row.without}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Avec Immovox */}
        <motion.div
          custom={1}
          variants={cardVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8"
        >
          <h3 className="text-sm font-bold uppercase tracking-widest text-primary mb-6">
            Avec Immovox
          </h3>
          <ul className="space-y-5">
            {criteria.map((row) => (
              <li key={row.with} className="flex gap-3 items-start">
                <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm text-foreground font-medium leading-relaxed">
                  {row.with}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      <div className="max-w-2xl mx-auto mt-12 text-center">
        <p className="text-muted-foreground leading-relaxed">
          {mode === "vente"
            ? "Immovox fonctionne en débordement : votre téléphone sonne d'abord. Si vous ne décrochez pas, l'appel est pris en charge. Quand vous êtes disponible, c'est vous qui répondez. Toujours."
            : "Renvoi total ou débordement : vous choisissez. Immovox qualifie chaque appel et ne vous remonte que les dossiers qui méritent votre attention."}
        </p>
      </div>
    </div>
  );
};

const AdsDifferentiation = ({ mode }: { mode: AdsMode }) => {
  return (
    <section className="section-padding bg-background">
      <div className="container mx-auto px-4">
        <DiffContent key={mode} mode={mode} />
      </div>
    </section>
  );
};

export default AdsDifferentiation;
